import { loadPdfjs } from '@/lib/pdfjs.js';

import type { OfficePreviewKind } from '../artifact-display.js';
import { registerOfficeFonts } from '../office-fonts.js';

/** The kinds whose thumbnail is a drawn first page, sheet or slide, kept as an image. */
export type RasterThumbnailKind = 'pdf' | OfficePreviewKind;

/** Width of a drawn thumbnail in pixels: twice a tile's width at most, so it stays sharp on a dense screen. */
export const THUMBNAIL_WIDTH = 320;

/**
 * Height a drawn PDF thumbnail never passes. A page takes its shape from the file, so a crafted page can be
 * thousands of times taller than wide; a tile shows only the top of a page, so the rest is never drawn.
 */
export const THUMBNAIL_MAX_HEIGHT = 2 * THUMBNAIL_WIDTH;

/** The top-left of the first sheet a workbook thumbnail shows. */
const SHEET_VIEWPORT = { row: 0, col: 0, rows: 30, cols: 12 };
const SHEET_HEIGHT = 200;

/** No web fonts from a CDN, as the full viewer: a thumbnail draws with the fonts the app serves. */
const SAFE_LOAD_OPTIONS = { useGoogleFonts: false };

/** Whether a preview kind has a drawn thumbnail. */
export function isRasterThumbnailKind(
  kind: string,
): kind is RasterThumbnailKind {
  return (
    kind === 'pdf' || kind === 'docx' || kind === 'xlsx' || kind === 'pptx'
  );
}

/** Encode the drawn canvas. WebP where the browser can write it; a browser that cannot falls back to PNG. */
function encodeCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('thumbnail encode failed')),
      'image/webp',
      0.8,
    );
  });
}

/** The scale that draws a page at the thumbnail width, refused for a page with no real, positive width. */
function thumbnailScale(pageWidth: number): number {
  if (!Number.isFinite(pageWidth) || pageWidth <= 0) {
    throw new Error('the PDF page has no width to scale');
  }
  return THUMBNAIL_WIDTH / pageWidth;
}

/**
 * Refuse an Office page the thumbnail cannot draw safely: one with no real, positive size, or one whose shape
 * would draw it taller than {@link THUMBNAIL_MAX_HEIGHT}. The library sizes its own canvas to the whole page,
 * so such a page is refused rather than drawn from its top.
 */
function refuseUndrawablePage(width: number, height: number): void {
  const drawnHeight = height * thumbnailScale(width);
  if (!(drawnHeight > 0 && drawnHeight <= THUMBNAIL_MAX_HEIGHT)) {
    throw new Error('the page cannot be drawn as a thumbnail');
  }
}

/**
 * Page one of a PDF, scaled to the thumbnail width, and no taller than {@link THUMBNAIL_MAX_HEIGHT}: a taller
 * page is drawn from its top. The document is destroyed whatever happens.
 */
async function drawPdf(
  canvas: HTMLCanvasElement,
  bytes: ArrayBuffer,
): Promise<void> {
  const pdfjs = await loadPdfjs();
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(bytes) });
  try {
    const page = await (await loadingTask.promise).getPage(1);
    const scale = thumbnailScale(page.getViewport({ scale: 1 }).width);
    const viewport = page.getViewport({ scale });
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.min(Math.ceil(viewport.height), THUMBNAIL_MAX_HEIGHT);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d context for the thumbnail');
    await page.render({ canvasContext: context, viewport }).promise;
  } finally {
    await loadingTask.destroy();
  }
}

/**
 * The first page, slide or sheet of an Office file, drawn by the same `@silurus/ooxml` library as the full
 * viewer but without one: the document classes draw straight onto a canvas that is never attached to the
 * page. The document is destroyed whatever happens.
 */
async function drawOffice(
  kind: OfficePreviewKind,
  canvas: HTMLCanvasElement,
  bytes: ArrayBuffer,
): Promise<void> {
  await registerOfficeFonts();
  const size = { width: THUMBNAIL_WIDTH, dpr: 1 };
  if (kind === 'docx') {
    const { DocxDocument } = await import('@silurus/ooxml/docx');
    const wordDocument = await DocxDocument.load(bytes, SAFE_LOAD_OPTIONS);
    try {
      const page = wordDocument.pageSize(0);
      refuseUndrawablePage(page.widthPt, page.heightPt);
      await wordDocument.renderPage(canvas, 0, size);
    } finally {
      wordDocument.destroy();
    }
    return;
  }
  if (kind === 'pptx') {
    const { PptxPresentation } = await import('@silurus/ooxml/pptx');
    const presentation = await PptxPresentation.load(bytes, SAFE_LOAD_OPTIONS);
    try {
      refuseUndrawablePage(presentation.slideWidth, presentation.slideHeight);
      await presentation.renderSlide(canvas, 0, {
        ...size,
        skipMediaControls: true,
      });
    } finally {
      presentation.destroy();
    }
    return;
  }
  const { XlsxWorkbook } = await import('@silurus/ooxml/xlsx');
  const workbook = await XlsxWorkbook.load(bytes, SAFE_LOAD_OPTIONS);
  try {
    const firstVisible = workbook.sheetNames.findIndex(
      (_, index) => !workbook.isHidden(index),
    );
    await workbook.renderViewport(
      canvas,
      Math.max(firstVisible, 0),
      SHEET_VIEWPORT,
      { ...size, height: SHEET_HEIGHT },
    );
  } finally {
    workbook.destroy();
  }
}

/**
 * Draw the first page of a PDF, the first page of a Word file, the first slide of a deck or the top of a
 * workbook's first visible sheet, {@link THUMBNAIL_WIDTH} pixels wide, and encode it as an image. Errors
 * from the renderers propagate, so the caller falls back to the file icon.
 */
export async function renderThumbnail(
  kind: RasterThumbnailKind,
  bytes: ArrayBuffer,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  if (kind === 'pdf') await drawPdf(canvas, bytes);
  else await drawOffice(kind, canvas, bytes);
  return encodeCanvas(canvas);
}
